<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { indexUserAddresses } from '@/lib/api/user_addresses.js'

import { useAuthenticationStore } from '@/stores/authentication.js'
import { useGeocodeStore } from '@/stores/geocode.js'
import { useServiceProviderSearchStore } from '@/stores/service_provider_search.js'
import SelectCity from '@/components/location/select_city.vue'

// Stores
const geocodeStore = useGeocodeStore()
const authenticationStore = useAuthenticationStore()
const serviceProviderSearchStore = useServiceProviderSearchStore()

// I18n
const { t } = useI18n()

// Variables
const locationSelectId = ref(0)
const locationSelect = computed(() => locationOptions.value[locationSelectId.value])
const selectCityRef = ref(null)
const typeFilterExpanded = ref(false)
const isRefreshing = ref(false)

const currentLocationDisplay = computed(() => {
  if (geocodeStore.isLoading) return t('service_providers.search_filters.loading')
  if (geocodeStore.isError) return t('service_providers.search_filters.location_unavailable')
  return geocodeStore.userLocation.nickname || t('service_providers.search_filters.loading')
})
const userAddresses = ref({})
const cityLocationOption = ref(null)
const pendingCityLocation = ref(null)
const cityLocationParsed = computed(() => {
  if (!pendingCityLocation.value) {
    return null
  }
  return {
    nickname: pendingCityLocation.value.full_name,
    location_name: pendingCityLocation.value.full_name,
    latitude: pendingCityLocation.value.lat,
    longitude: pendingCityLocation.value.lon
  }
})
const locationOptions = computed(() => ({ "-1": cityLocationParsed.value, 0: geocodeStore.userLocation, ...userAddresses.value }))
const locationOptionsSelect = computed(() => {
  const filtered = Object.entries(locationOptions.value).filter(([key]) => key > 0)
  return filtered.sort(([, a], [, b]) => {
    if (a.primary && !b.primary) return -1
    if (!a.primary && b.primary) return 1
    return (a.nickname || '').localeCompare(b.nickname || '')
  })
})

async function getUserAddresses() {
  let params = new URLSearchParams()
  params.set('query[user_id_eq]', authenticationStore.currentUser.user.id)
  params.set('query[archived_eq]', 'false')
  params.set('query[is_ephemeral_eq]', 'false')

  await indexUserAddresses(params)
    .then((response) => {
      // Converting Array to Object with the ID being the key
      userAddresses.value = response.reduce((acc, item) => {
        acc[item.id] = item
        return acc
      }, {})
    })
}

function setLocation(locationId) {
  locationSelectId.value = locationId
  if (locationId === '-1' && cityLocationOption.value) {
    pendingCityLocation.value = cityLocationOption.value
  }
}

function clearOtherLocation(event) {
  event.stopPropagation()
  pendingCityLocation.value = null
  if (selectCityRef.value && selectCityRef.value.clearSearch) {
    selectCityRef.value.clearSearch()
  }
  setLocation(0)
}

async function refreshCurrentLocation(event) {
  event.stopPropagation()
  isRefreshing.value = true
  geocodeStore.$reset()
  
  // Show toast notification
  try {
    const { useToastStore } = await import('@/stores/toast.js');
    const toastStore = useToastStore();
    toastStore.success(t('toast.location_refreshed'));
  } catch (e) {
    console.log('Toast notification failed:', e);
  }
  
  setTimeout(() => {
    isRefreshing.value = false
  }, 1000)
}

// Lifecycle
onMounted(async () => {
  await getUserAddresses()
  
  // Set default location to primary address if available
  const primaryAddress = Object.entries(userAddresses.value).find(([_, addr]) => addr.primary)
  if (primaryAddress) {
    setLocation(primaryAddress[0])
  }
})

watch(locationSelect, (location, old) => {
  if (location != old) {
    serviceProviderSearchStore.filters.location = location
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('user_location', JSON.stringify(location))
    }
  }
})

watch(cityLocationOption, (newCity) => {
  if (newCity && locationSelectId.value === '-1') {
    pendingCityLocation.value = newCity
    serviceProviderSearchStore.filters.location = cityLocationParsed.value
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('user_location', JSON.stringify(cityLocationParsed.value))
    }
    cityLocationOption.value = null
  }
})

</script>

<template>
  <div>
    <div class="p-4 pl-0">
      <h2 class="uppercase text-lg">{{ t('service_providers.search_filters.filter_title') }}</h2>
    </div>

    <div class="border-t-2 location-filter">
      <div class="p-4 pl-0">
        <h3 class="text-lg">{{ t('service_providers.search_filters.location_filter') }}</h3>
      </div>

      <ul>
        <!-- Saved Locations -->
        <li v-for="[index, location] in locationOptionsSelect" :key="index"
            class="pl-4 py-2 flex items-center gap-2" :class="{'active': locationSelectId === index}"
            @click="setLocation(index)">
          <svg class="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z"/>
          </svg>
          <span>{{ location.nickname }}</span>
          <span v-if="location.primary" class="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded font-semibold ml-auto mr-4">{{ t('service_providers.search_filters.primary') }}</span>
        </li>

        <!-- Current Geolocation -->
        <li class="pl-4 py-2 flex justify-between items-center" :class="{'active': locationSelectId === 0}"
            @click="setLocation(0)">
          <div class="flex items-center gap-2">
            <svg class="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/>
            </svg>
            <span>{{ currentLocationDisplay }}</span>
          </div>
          <button @click="refreshCurrentLocation" class="bg-black/50 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-black/70 mr-4">
            <span :class="{ 'animate-spin': geocodeStore.isLoading || isRefreshing }">↻</span>
          </button>
        </li>

        <!-- Other Location (City Search) -->
        <li v-if="pendingCityLocation" class="pl-4 py-2 flex justify-between items-center" :class="{'active': locationSelectId === '-1'}"
            @click="setLocation('-1')">
          <div class="flex items-center gap-2">
            <svg class="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
            </svg>
            <span>{{ pendingCityLocation.full_name }}</span>
          </div>
          <button @click="clearOtherLocation" class="bg-black/50 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-black/70 mr-4">×</button>
        </li>

        <li
            class="pl-4 py-2 pr-4" :class="{'active': locationSelectId === '-1' && !pendingCityLocation}"
            @click="setLocation('-1')">
          <span>{{ t('service_providers.search_filters.other_location') }}</span>
        </li>
        <li class="pl-4 py-2 pr-4" @click="setLocation('-1')">
          <SelectCity ref="selectCityRef" @click="setLocation('-1')" v-model="cityLocationOption" />
        </li>
      </ul>
    </div>

    <div class="border-t-2 type-filter">
      <div class="p-4 pl-0 cursor-pointer flex justify-between items-center" @click="typeFilterExpanded = !typeFilterExpanded">
        <div>
          <h3 class="text-lg">{{ t('service_providers.search_filters.type_filter') }}</h3>
          <p class="text-sm text-gray-600 mt-1">{{ t('service_providers.search_filters.type_filter_description') }}</p>
        </div>
        <span class="text-xl">{{ typeFilterExpanded ? '−' : '+' }}</span>
      </div>

      <Transition name="slide-down">
        <div v-show="typeFilterExpanded">
          <label class="pl-4 py-2 flex flex-row items-center">
            <input type="checkbox" class="w-4 h-4 mr-4" v-model="serviceProviderSearchStore.filters.type.dealership" />
            <span>{{ t('service_providers.search_filters.service_chain_dealership') }}</span>
          </label>
          <label class="pl-4 py-2 flex flex-row items-center">
            <input type="checkbox" class="w-4 h-4 mr-4" v-model="serviceProviderSearchStore.filters.type.chain" />
            <span>{{ t('service_providers.search_filters.service_chain_chain') }}</span>
          </label>
          <label class="pl-4 py-2 flex flex-row items-center">
            <input type="checkbox" class="w-4 h-4 mr-4" v-model="serviceProviderSearchStore.filters.type.independent" />
            <span>{{ t('service_providers.search_filters.service_chain_independent') }}</span>
          </label>
        </div>
      </Transition>

    </div>

  </div>
</template>


<style lang="scss" scoped>
@import '@/assets/styles/views/service_providers/search_filters';

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
  max-height: 200px;
  overflow: hidden;
}

.slide-down-enter-from,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
